import { AGE_LABELS } from "../data/eventOptions";

export const normalizeText = (value) =>
  (value || "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

export const isValidDate = (value) => {
  if (!value) return false;

  const date = new Date(value);
  return !Number.isNaN(date.getTime());
};

export const formatDateInput = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 10);
};

export const formatDateFR = (value) => {
  if (!isValidDate(value)) return "—";

  return new Date(value).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

export const formatDateRangeFR = (start, end) => {
  if (!isValidDate(start)) return "—";

  if (!isValidDate(end)) {
    return formatDateFR(start);
  }

  const startLabel = formatDateFR(start);
  const endLabel = formatDateFR(end);

  if (startLabel === endLabel) {
    return startLabel;
  }

  return `${startLabel} → ${endLabel}`;
};

export const shortText = (value = "", maxLength = 140) => {
  const text = String(value || "").trim();

  if (text.length <= maxLength) {
    return text;
  }

  return `${text.slice(0, maxLength).trim()}…`;
};

export const toCanonicalAge = (value = "") => {
  const normalized = normalizeText(value);

  if (normalized.includes("eveil")) {
    return "Éveil";
  }

  if (normalized.includes("mini") && normalized.includes("poussin")) {
    return "Mini-Poussin(e)";
  }

  if (!normalized.includes("mini") && normalized.includes("poussin")) {
    return "Poussin(e)";
  }

  if (normalized.includes("benjamin")) {
    return "Benjamin(e)";
  }

  if (normalized.includes("minime")) {
    return "Minime";
  }

  if (normalized.includes("cadet")) {
    return "Cadet(te)";
  }

  if (normalized.includes("junior")) {
    return "Junior";
  }

  if (normalized.includes("senior")) {
    return "Sénior";
  }

  if (normalized.includes("veteran")) {
    return "Vétéran";
  }

  if (normalized.includes("adult")) {
    return "ADULT";
  }

  if (normalized.includes("u-21")) {
    return "U-21";
  }

  if (normalized.includes("u-18")) {
    return "U-18";
  }

  if (normalized.includes("u-16")) {
    return "U-16";
  }

  if (normalized.includes("u-14")) {
    return "U-14";
  }

  if (normalized.includes("u-12")) {
    return "U-12";
  }

  if (normalized.includes("u-10")) {
    return "U-10";
  }

  if (normalized.includes("u-8")) {
    return "U-8";
  }

  return null;
};

export const parseEventCategories = (cateEvent = "") => {
  const values = Array.isArray(cateEvent)
    ? cateEvent
    : String(cateEvent || "").split(/[,/|;]+/g);

  const uniqueValues = new Set();

  values.forEach((value) => {
    const trimmedValue = String(value || "").trim();

    if (!trimmedValue) return;

    uniqueValues.add(toCanonicalAge(trimmedValue) || trimmedValue);
  });

  return Array.from(uniqueValues).sort((a, b) => {
    const indexA = AGE_LABELS.indexOf(a);
    const indexB = AGE_LABELS.indexOf(b);

    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }

    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;

    return a.localeCompare(b, "fr");
  });
};

export const eventHasAgeCategory = (event, selectedCategory) => {
  if (!selectedCategory) return true;

  const categories = parseEventCategories(event?.cate_event);
  const canonicalCategory =
    toCanonicalAge(selectedCategory) || selectedCategory;

  return categories.some(
    (category) => normalizeText(category) === normalizeText(canonicalCategory),
  );
};

export const uniqueSortedValues = (values = []) =>
  Array.from(
    new Set(values.map((value) => String(value || "").trim()).filter(Boolean)),
  ).sort((a, b) => a.localeCompare(b, "fr"));

export const toBoolean = (value, fallback = true) => {
  if (value === undefined || value === null || value === "") {
    return fallback;
  }

  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;

  const normalized = normalizeText(value);

  if (["false", "0", "non", "inactif"].includes(normalized)) {
    return false;
  }

  if (["true", "1", "oui", "actif"].includes(normalized)) {
    return true;
  }

  return fallback;
};
